import React, { useState, useEffect } from 'react';
import { getCacheInfo, debugCache, clearTenantCache } from '../../services/tenantCache';
import { useTenantContext } from '../../context/TenantContext';
import { RefreshCw, Trash2, Info, Database, Clock } from 'lucide-react';

/**
 * Panneau de debug pour le cache tenant (développement uniquement)
 */
export const CacheDebugPanel: React.FC = () => {
  const { tenant, refreshTenant, clearCache } = useTenantContext();
  const [cacheInfo, setCacheInfo] = useState(getCacheInfo());
  const [isVisible, setIsVisible] = useState(false);

  // Mettre à jour les infos du cache périodiquement
  useEffect(() => {
    const interval = setInterval(() => {
      setCacheInfo(getCacheInfo());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Ne pas afficher en production
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleDebugCache = () => {
    debugCache();
    setCacheInfo(getCacheInfo());
  };

  const handleClearCache = () => {
    clearCache();
    setCacheInfo(getCacheInfo());
  };

  const handleRefreshTenant = () => {
    refreshTenant();
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Bouton toggle */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all duration-200"
        title="Debug Cache Tenant"
      >
        <Database className="w-5 h-5" />
      </button>

      {/* Panneau de debug */}
      {isVisible && (
        <div className="absolute bottom-16 right-0 bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-80 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Database className="w-5 h-5 mr-2" />
              Cache Debug
            </h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>

          {/* Informations du cache */}
          <div className="space-y-3 mb-4">
            <div className="bg-gray-50 p-3 rounded-md">
              <h4 className="font-medium text-gray-700 mb-2 flex items-center">
                <Info className="w-4 h-4 mr-1" />
                État du Cache
              </h4>
              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span>Cache Mémoire:</span>
                  <span className={cacheInfo.hasMemoryCache ? 'text-green-600' : 'text-red-600'}>
                    {cacheInfo.hasMemoryCache ? '✓ Actif' : '✗ Vide'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>SessionStorage:</span>
                  <span className={cacheInfo.hasSessionCache ? 'text-green-600' : 'text-red-600'}>
                    {cacheInfo.hasSessionCache ? '✓ Actif' : '✗ Vide'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Tenant ID:</span>
                  <span className="font-mono text-xs">
                    {cacheInfo.tenantId || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Version:</span>
                  <span className="font-mono text-xs">
                    {cacheInfo.version ? cacheInfo.version.split('_')[1] : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Informations du tenant actuel */}
            {tenant && (
              <div className="bg-blue-50 p-3 rounded-md">
                <h4 className="font-medium text-blue-700 mb-2">Tenant Actuel</h4>
                <div className="text-sm space-y-1">
                  <div><strong>ID:</strong> {tenant.id}</div>
                  <div><strong>Nom:</strong> {tenant.name}</div>
                  <div><strong>Email:</strong> {tenant.email}</div>
                  <div><strong>Logo:</strong> {tenant.logo ? '✓' : '✗'}</div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="space-y-2">
            <button
              onClick={handleDebugCache}
              className="w-full flex items-center justify-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <Info className="w-4 h-4 mr-2" />
              Debug Console
            </button>
            
            <button
              onClick={handleClearCache}
              className="w-full flex items-center justify-center px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Vider Cache
            </button>
            
            <button
              onClick={handleRefreshTenant}
              className="w-full flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Rafraîchir Tenant
            </button>
          </div>

          {/* Timestamp */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center text-xs text-gray-500">
              <Clock className="w-3 h-3 mr-1" />
              Dernière MAJ: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
